import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Crown, Sparkles, Check, Lock, Medal, Gift, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataSaver } from "@/context/DataSaverContext";

const BADGE_STYLES = {
  "Crown Oracle": "bg-gold text-obsidian",
  "Super Fan": "bg-emerald-accent/25 text-emerald-accent",
  "True Believer": "bg-rose-400/20 text-rose-300",
  "Fan": "border border-gold/30 text-gold/80",
};

export default function Predictions() {
  const { user } = useAuth();
  const { dataSaver } = useDataSaver();
  const [candidates, setCandidates] = useState([]);
  const [picks, setPicks] = useState([]);
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);
  const [spotlight, setSpotlight] = useState(null);
  const [state, setState] = useState({ open: true, scored: false });
  const [saving, setSaving] = useState(false);

  const loadAll = () => {
    api.get("/candidates?limit=40").then((r) => setCandidates(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => {});
    api.get("/predictions/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/predictions/state").then((r) => setState(r.data)).catch(() => {});
    api.get("/predictions/spotlight").then((r) => setSpotlight(r.data.champion)).catch(() => {});
    if (user) api.get("/predictions/me").then((r) => { setMe(r.data); if (r.data.picks?.length) setPicks(r.data.picks); }).catch(() => {});
  };
  useEffect(loadAll, [user]);

  const toggle = (id) => {
    if (state.scored) return;
    setPicks((p) => p.includes(id) ? p.filter((x) => x !== id) : (p.length < 5 ? [...p, id] : p));
  };

  const submit = async () => {
    if (picks.length === 0) { toast.error("Pick at least one candidate"); return; }
    setSaving(true);
    try { await api.post("/predictions", { picks }); toast.success("Prediction locked in!", { description: "Good luck — check the leaderboard on finale night." }); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const byId = Object.fromEntries(candidates.map((c) => [c.id, c]));

  return (
    <div className="section py-12 sm:py-16" data-testid="predictions-page">
      <p className="kicker mb-2 flex items-center gap-2"><Trophy className="h-4 w-4" /> Fan Predictions</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50">Predict the Crown</h1>
      <p className="mt-3 max-w-2xl text-amber-100/60">Pick your Top 5 in order. Earn <span className="text-gold">10 points</span> for each candidate in the exact position, <span className="text-gold">3 points</span> for any correct Top-5 pick — and climb the super-fan leaderboard.</p>

      {/* Champion spotlight */}
      {spotlight && (
        <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border border-gold/40 bg-gradient-to-r from-gold/15 to-transparent p-6" data-testid="champion-spotlight">
          <Star className="h-8 w-8 shrink-0 text-gold" fill="currentColor" />
          <div>
            <p className="text-[0.6rem] uppercase tracking-widest text-gold/70">Round {spotlight.round} Champion Predictor</p>
            <p className="font-serif text-2xl text-amber-50">{spotlight.name}</p>
            <p className="text-sm text-amber-100/60">{spotlight.points} points · <span className="text-gold">{spotlight.badge}</span></p>
          </div>
        </div>
      )}

      {/* Your prize */}
      {me?.prize && (
        <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-emerald-accent/40 bg-emerald-accent/10 p-6" data-testid="prediction-prize">
          <Gift className="h-8 w-8 shrink-0 text-emerald-accent" />
          <div className="flex-1">
            <p className="font-serif text-xl text-amber-50">🎉 You're the top predictor!</p>
            <p className="text-sm text-amber-100/70">Enjoy <span className="text-emerald-accent font-bold">{me.prize.discount_pct}% off</span> in the Official Store — use your code at checkout:</p>
          </div>
          <div className="flex items-center gap-2">
            <code className="rounded-lg border border-emerald-accent/40 bg-obsidian px-4 py-2 font-mono text-lg text-emerald-accent" data-testid="prize-code">{me.prize.code}</code>
            <Link to="/shop" className="btn-gold text-xs">Shop now</Link>
          </div>
        </div>
      )}

      {!user && (
        <div className="mt-8 glass rounded-2xl p-8" data-testid="predictions-login-gate">
          <p className="flex items-center gap-2 text-amber-100/80"><Lock className="h-5 w-5 text-gold" /> <Link to="/login" className="text-gold hover:underline">Sign in</Link> to lock in your predictions and earn badges.</p>
        </div>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-3">
        {/* Picker */}
        <div className="lg:col-span-2">
          {me?.scored ? (
            <div className="card-lux p-6" data-testid="prediction-result">
              <div className="flex items-center justify-between">
                <p className="kicker">Your Result</p>
                {me.badge && <span className={`rounded-full px-3 py-1 text-xs font-bold ${BADGE_STYLES[me.badge]}`}>{me.badge}</span>}
              </div>
              <p className="mt-3 font-serif text-4xl text-gold">{me.points} <span className="text-lg text-amber-100/60">points</span></p>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
                {me.picks.map((id, i) => (
                  <div key={id} className={`rounded-xl border p-2 text-center ${me.correct?.includes(id) ? "border-emerald-accent bg-emerald-accent/10" : "border-gold/20 opacity-60"}`}>
                    <p className="text-[0.6rem] text-gold/70">#{i + 1}</p>
                    <p className="mt-1 text-xs text-amber-50 truncate">{byId[id]?.name || "—"}</p>
                    {me.correct?.includes(id) && <Check className="mx-auto mt-1 h-3 w-3 text-emerald-accent" />}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="pick-slots">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${picks[i] ? "border-gold bg-gold/10 text-amber-50" : "border-gold/20 text-muted-foreground"}`}>
                    <span className="font-mono text-gold">#{i + 1}</span>{picks[i] ? (byId[picks[i]]?.name || "…") : "empty"}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {candidates.map((c) => {
                  const idx = picks.indexOf(c.id);
                  const selected = idx >= 0;
                  return (
                    <button key={c.id} onClick={() => toggle(c.id)} disabled={state.scored} data-testid={`pick-${c.slug}`}
                      className={`group relative overflow-hidden rounded-xl border text-left transition-all ${selected ? "border-gold ring-2 ring-gold/40" : "border-gold/20 hover:border-gold/50"}`}>
                      <div className="aspect-[3/4] overflow-hidden bg-obsidian-600">
                        {!dataSaver && c.portrait_url && <img src={c.portrait_url} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />}
                      </div>
                      {selected && <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full gold-bg text-xs font-bold text-obsidian">{idx + 1}</span>}
                      <div className="p-2">
                        <p className="text-xs text-amber-50 truncate">{c.name}</p>
                        <p className="text-[0.6rem] text-muted-foreground truncate">{c.delegation_name}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
              {user && !state.scored && (
                <button onClick={submit} disabled={saving} data-testid="submit-prediction" className="btn-gold mt-6 inline-flex items-center gap-2">
                  <Sparkles className="h-4 w-4" /> {me?.picks?.length ? "Update my prediction" : "Lock in my Top 5"}
                </button>
              )}
            </>
          )}
        </div>

        {/* Leaderboard */}
        <div>
          <p className="kicker mb-4 flex items-center gap-2"><Medal className="h-4 w-4" /> Super-Fan Leaderboard</p>
          <div className="card-lux divide-y divide-gold/10" data-testid="prediction-leaderboard">
            {board.length === 0 && <p className="p-4 text-sm text-muted-foreground">Be the first to predict.</p>}
            {board.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-3">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "gold-bg text-obsidian" : "border border-gold/25 text-gold"}`}>{i === 0 ? <Crown className="h-3.5 w-3.5" /> : i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-amber-50">{r.name}</p>
                  {r.badge && <span className={`inline-block rounded-full px-2 py-0.5 text-[0.55rem] ${BADGE_STYLES[r.badge]}`}>{r.badge}</span>}
                </div>
                <span className="font-mono text-sm text-gold">{r.points}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
